import type { Asset } from '@compass/client/types/asset';
import { AssetType, AssetUploadStatus } from '@compass/client/types/asset';
import { assetsService } from '@services/assets.service';
import { useStore } from '@store/store';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const READY_ASSET_STATUSES = new Set<AssetUploadStatus>([
  AssetUploadStatus.ASSET_UPLOAD_STATUS_COMPLETED,
  AssetUploadStatus.ASSET_UPLOAD_STATUS_UPLOADED,
]);

interface CaseRecordContext {
  organizationId: string;
  programId: string;
  caseRecordId: string;
}

/**
 * Hook to fetch assets for a call, with support for caching and polling
 *
 * This hook first checks if assets are available in the cached call data
 * (from the `assets` field in the call response), and falls back to making
 * an API call to fetch assets if not found in cache.
 *
 * @param caseRecordId - The ID of the case record
 * @param callId - The ID of the call to fetch assets for
 * @param assetType - Optional specific asset type to filter by
 * @param options - Optional configuration for polling behavior
 * @returns Query result containing array of assets
 */
export const useCallAssets = (
  caseRecordId: string | null,
  callId: string | null,
  assetType?: AssetType,
  options?: {
    pollingInterval?: number;
    stopPollingWhenFound?: boolean;
    requireUploadComplete?: boolean;
  },
  // eslint-disable-next-line complexity
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const pollingInterval = options?.pollingInterval ?? 10000; // Default 10 seconds
  const stopPollingWhenFound = options?.stopPollingWhenFound ?? true;
  const requireUploadComplete = options?.requireUploadComplete ?? false;

  const context: CaseRecordContext | null = useMemo(() => {
    if (!caseRecordId || !selectedOrganizationId || !selectedProgramId) return null;
    return {
      organizationId: selectedOrganizationId,
      programId: selectedProgramId,
      caseRecordId,
    };
  }, [caseRecordId, selectedOrganizationId, selectedProgramId]);

  return useQuery<Asset[], Error>({
    queryKey: [
      'callAssets',
      caseRecordId,
      callId,
      assetType ?? 'all',
      requireUploadComplete,
      testModeEnabled,
    ],
    queryFn: async () => {
      // Return empty response if in test mode since calls are not supported in sandbox
      if (testModeEnabled) {
        return [];
      }

      const assets = await assetsService.listAssets(context!, callId!, assetType);
      if (!requireUploadComplete) {
        return assets;
      }
      // When the caller needs a ready-to-consume asset (e.g., audio player),
      // filter out assets still uploading. The asset record is created early
      // in the lifecycle, so without this gate the player would render against
      // a recording that isn't downloadable yet. Polling continues until a
      // ready asset arrives (the length-based stopPollingWhenFound check).
      return assets.filter(
        asset => asset.status !== undefined && READY_ASSET_STATUSES.has(asset.status),
      );
    },
    enabled: !!context && !!callId && !testModeEnabled,
    refetchInterval: stopPollingWhenFound
      ? query => {
          // If assets found and stopPollingWhenFound is true, stop polling
          // Otherwise, continue polling at the specified interval
          const assets = query.state.data;
          return !assets || assets.length === 0 ? pollingInterval : false;
        }
      : pollingInterval, // Fixed polling interval if stopPollingWhenFound is false
  });
};
